/// <amd-module name="Repos/Views/PullRequest/Util/Check" />
import { GitPullRequest, GitPullRequestStatus, GitStatusState, IdentityRefWithVote, PullRequestStatus } from "Repos/Common/Contracts/Git";
import { PolicyEvaluationStatus } from "Repos/Common/Contracts/Policy";
import { getBuildUrl } from "Repos/Common/Util/Build";
import { getDisplayNameFromIdentity } from "Repos/Common/Util/Identity";
import {
    BuildPolicyEvaluation,
    CheckEvaluation,
    ChecksSummary,
    IApproverCountPolicyEvaluation,
    ICompletionStatus,
    IPullRequestCombinedPolicyEvaluation,
    IPullRequestPolicyEvaluation,
    IReviewer,
    IStatusPolicyEvaluation,
    PolicyTypeId,
    ReviewerVote,
    ReviewersSummary
} from "Repos/Views/PullRequest/Contracts/PullRequest";
import * as Resources from "Repos/Views/PullRequest/Resources";
import { getTFSProject } from "Tfs/Platform/Page";
import { ObservableCollection } from "VSS/Core/Observable";
import { format, localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { addMenuItems } from "VSS/Features/PlatformUI/ContributedMenu";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IMenuItem } from "VSSUI/Menu";
import { IStatusProps, Statuses } from "VSSUI/Status";
import { IPullRequestAutoCompleteData } from "../PullRequestDataService";
import { PullRequestPermissions, hasPermission } from "../PullRequestPermissionService";
import { getCheckPolicies } from "./Policy";
import { getPendingVotes, getVoteStatusProps, hasApproved } from "./Vote";

const maxPreviewChecks = 6;
const pullRequestStatusMenuContributionId = "ms.vss-code-web.pull-request-status-menu";

export interface IStatusExtensionContext {
    status: GitPullRequestStatus;
    pullRequest: GitPullRequest;
}

export function getChecksSummary(checks: CheckEvaluation[], completionStatus: ICompletionStatus): ChecksSummary {
    const didBypassPolicies = Boolean(completionStatus.completedData && completionStatus.completedData.completionOptions.bypassPolicy);
    let primaryChecks: CheckEvaluation[];
    let secondaryChecks: CheckEvaluation[];

    if (!completionStatus.autoCompletingData) {
        primaryChecks = checks.filter(evaluation => evaluation.isRequired);
        secondaryChecks = checks.filter(evaluation => !evaluation.isRequired);
    } else {
        // Find policies which auto-complete would block on which are not already in the ignored list
        const blockingPolicyIds = new Set(completionStatus.autoCompletingData.blockingPolicyIds || []);

        // Find policies which are being ignored by ignored auto-complete
        const ignoredPolicyIds = new Set(
            (completionStatus.autoCompletingData.completionOptions &&
                completionStatus.autoCompletingData.completionOptions.autoCompleteIgnoreConfigIds) ||
                []
        );

        const { blockingChecks, nonBlockingChecks } = computeAutoCompleteBlockers(checks, blockingPolicyIds, ignoredPolicyIds);

        primaryChecks = blockingChecks;
        secondaryChecks = nonBlockingChecks;
    }

    let previewChecks =
        primaryChecks.length === 1
            ? primaryChecks
            : checks.length === 1
            ? checks
            : [
                  ...primaryChecks.filter(evaluation => evaluation.status === PolicyEvaluationStatus.Rejected),
                  ...primaryChecks.filter(evaluation => evaluation.status === PolicyEvaluationStatus.Broken),
                  ...primaryChecks.filter(evaluation => evaluation.status === PolicyEvaluationStatus.Running),
                  ...primaryChecks.filter(evaluation => evaluation.status === PolicyEvaluationStatus.Queued)
              ];

    const max = previewChecks.length < checks.length ? maxPreviewChecks - 1 : maxPreviewChecks;
    if (previewChecks.length > max) {
        previewChecks = previewChecks.slice(0, maxPreviewChecks - 1);
    }

    const [primaryPolicyStatus, primaryMessage] = getRequiredChecksMessage(primaryChecks);
    const [secondaryPolicyStatus, secondaryMessage] = getOptionalChecksMessage(secondaryChecks);

    return {
        didBypassPolicies,
        checksCount: checks.length,
        previewChecks,
        hasMoreChecks: previewChecks.length < checks.length,
        primaryStatus: checkStatusMap[primaryPolicyStatus][0],
        primaryMessage,
        secondaryStatus:
            policyStatusPriorities[primaryPolicyStatus] > policyStatusPriorities[secondaryPolicyStatus]
                ? checkStatusMap[secondaryPolicyStatus][0]
                : undefined,
        secondaryMessage
    };
}

export function getReviewersSummary(
    pageContext: IVssPageContext,
    evaluations: IPullRequestPolicyEvaluation[],
    reviewers: IReviewer[],
    creatorId: string,
    lastPusherId: string,
    isDraft: boolean
): ReviewersSummary {
    if (isDraft) {
        return { message: Resources.NoRequiredReviewerInDraftMode, status: Statuses.Queued };
    }

    const individualReviewers = reviewers.filter(r => !r.baseReviewer.isContainer);

    // Negative votes block if ApprovalCount policy is enabled and allowDownVotes isn't set
    const approvalCountPolicy = getMostRestrictiveApprovalCountEvaluation(evaluations);
    if (approvalCountPolicy && !approvalCountPolicy.allowDownvotes) {
        const negativeVotesSummary = summarizeNegativeVotes(individualReviewers.map(r => r.baseReviewer));
        if (negativeVotesSummary) {
            return negativeVotesSummary;
        }
    }

    const approvals = individualReviewers.filter(r => hasApproved(r.vote));
    const remainderApprovalCount = approvalCountPolicy ? approvalCountPolicy.minimumApproverCount - approvals.length : 0;

    const pendingRequiredReviewers = reviewers.filter(r => r.baseReviewer.isRequired && (!hasApproved(r.vote) || getPendingVotes(r, creatorId) > 0));
    if (pendingRequiredReviewers.length > 0) {
        // If there are negative votes among the required reviewers, they get priority above any pending votes
        // Let's group, without duplicates, the individual votes that belong to required reviewers
        const individualRequiredReviewers: { [id: string]: IdentityRefWithVote } = {};
        for (const reviewer of pendingRequiredReviewers) {
            if (reviewer.baseReviewer.isContainer) {
                if (reviewer.votedForBy) {
                    for (const votedBy of reviewer.votedForBy) {
                        individualRequiredReviewers[votedBy.id] = votedBy;
                    }
                }
            } else {
                individualRequiredReviewers[reviewer.baseReviewer.id] = reviewer.baseReviewer;
            }
        }

        const negativeVotesSummary = summarizeNegativeVotes(Object.keys(individualRequiredReviewers).map(key => individualRequiredReviewers[key]));
        if (negativeVotesSummary) {
            return negativeVotesSummary;
        }

        if (isFeatureFlagEnabled(pageContext, "Git.PullRequests.ShowRequiredReviewersBesidesAuthorMustApprove", false)) {
            if (remainderApprovalCount <= pendingRequiredReviewers.length) {
                const authorReviewer = individualReviewers.find(r => r.identity.id === creatorId);
                const besidesAuthor = authorReviewer
                    && approvalCountPolicy !== undefined
                    && !approvalCountPolicy.creatorVoteCounts
                    && authorReviewer.vote > 0;
                if (besidesAuthor) {
                    return {
                        message: format(Resources.RequiredReviewersBesidesAuthorNeedToApprove, approvalCountPolicy.minimumApproverCount),
                        status: Statuses.Waiting
                    };
                }
            }
        }

        if (pendingRequiredReviewers.length === 1 && remainderApprovalCount <= 1) {
            if (pendingRequiredReviewers[0].baseReviewer.isContainer) {
                const soloPendingVotes = getPendingVotes(pendingRequiredReviewers[0], creatorId);
                return {
                    message: soloPendingVotes > 1 ? Resources.RequiredReviewerGroupNeedsToApproveMany : Resources.RequiredReviewerGroupNeedsToApprove,
                    soloDisplayName: getDisplayNameFromIdentity(pendingRequiredReviewers[0].baseReviewer),
                    soloPendingVotes,
                    status: Statuses.Waiting
                };
            } else {
                return {
                    message: Resources.RequiredReviewerIndividualNeedsToApprove,
                    soloDisplayName: pendingRequiredReviewers[0].identity.displayName,
                    status: Statuses.Waiting
                };
            }
        }

        if (remainderApprovalCount <= pendingRequiredReviewers.length) {
            return { message: format(Resources.RequiredReviewersNeedToApprove, pendingRequiredReviewers.length), status: Statuses.Waiting };
        }
    }

    const blocks = evaluations.filter(evaluation => evaluation.isBlocking);
    const requiredReviewerBlocks = blocks.filter(block => block.policyType.id === PolicyTypeId.automaticReviewers);
    const requiredReviewerFailures = requiredReviewerBlocks.filter(block => block.status !== PolicyEvaluationStatus.Approved);
    if (requiredReviewerFailures.length > 0) {
        // This case should not happen as it's covered by checking required `reviewers`,
        // but to avoid any leak (reviewers out of sync), we check it here again and return the policy message.
        return { message: requiredReviewerFailures[0].displayText, status: Statuses.Waiting };
    }

    if (approvalCountPolicy && !approvalCountPolicy.isMinimumApproverCountSatisfied) {
        const authorReviewer = individualReviewers.find(r => r.identity.id === creatorId);
        const lastPusherReviewer = individualReviewers.find(r => r.identity.id === lastPusherId);
        const besidesAuthor = !approvalCountPolicy.creatorVoteCounts && authorReviewer && authorReviewer.vote > 0;
        const besidesLastPusher = approvalCountPolicy.blockLastPusherVote && lastPusherReviewer && lastPusherReviewer.vote > 0;
        let message;
        if (approvalCountPolicy.minimumApproverCount === 1) {
            message =
                besidesAuthor && besidesLastPusher
                    ? Resources.AtLeastOneReviewerBesidesAuthorAndLastPusherMustApprove
                    : besidesAuthor
                    ? Resources.AtLeastOneReviewerBesidesAuthorMustApprove
                    : besidesLastPusher
                    ? Resources.AtLeastOneReviewerBesidesLastPusherMustApprove
                    : Resources.AtLeastOneReviewerMustApprove;
        } else {
            message =
                besidesAuthor && besidesLastPusher
                    ? Resources.AtLeastReviewersBesidesAuthorAndLastPusherMustApprove
                    : besidesAuthor
                    ? Resources.AtLeastReviewersBesidesAuthorMustApprove
                    : besidesLastPusher
                    ? Resources.AtLeastReviewersBesidesLastPusherMustApprove
                    : Resources.AtLeastReviewersMustApprove;
        }

        return { message: format(message, approvalCountPolicy.minimumApproverCount), status: Statuses.Waiting };
    }


    if (approvalCountPolicy?.needsVoteOnLastIteration) {
        return { message: Resources.AtLeastOneReviewerMustApproveLastIteration, status: Statuses.Waiting };
    }


    if (approvalCountPolicy?.hasNotApprovedIterations) {
        return { message: Resources.PolicyAllIterationsApproved, status: Statuses.Waiting };
    }

    if (requiredReviewerBlocks.length > 0) {
        return { message: requiredReviewerBlocks[0].displayText, status: Statuses.Success };
    } else if (approvalCountPolicy) {
        return { message: approvalCountPolicy.displayText, status: Statuses.Success };
    } else {
        return { message: Resources.NoReviewRequired, status: Statuses.Success, isEmpty: true };
    }
}

function summarizeNegativeVotes(reviewers: IdentityRefWithVote[]): ReviewersSummary | undefined {
    const rejectingReviewers = reviewers.filter(r => r.vote === ReviewerVote.Rejected);
    if (rejectingReviewers.length > 0) {
        if (rejectingReviewers.length === 1) {
            return {
                message: Resources.ReviewerOneRejected,
                soloDisplayName: rejectingReviewers[0].displayName,
                status: getVoteStatusProps(ReviewerVote.Rejected)
            };
        } else {
            return { message: format(Resources.ReviewersManyRejected, rejectingReviewers.length), status: Statuses.Failed };
        }
    }

    const waitingForAuthorReviewers = reviewers.filter(r => r.vote === ReviewerVote.WaitingForAuthor);
    if (waitingForAuthorReviewers.length > 0) {
        if (waitingForAuthorReviewers.length === 1) {
            return {
                message: Resources.ReviewerOneWaitingForAuthor,
                soloDisplayName: waitingForAuthorReviewers[0].displayName,
                status: getVoteStatusProps(ReviewerVote.WaitingForAuthor)
            };
        } else {
            return { message: format(Resources.ReviewersManyWaitingForAuthor, waitingForAuthorReviewers.length), status: Statuses.Warning };
        }
    }
}

export function getMostRestrictiveApprovalCountEvaluation(evaluations: IPullRequestPolicyEvaluation[]): IApproverCountPolicyEvaluation | undefined {
    // We may have more than one evaluation if we inherit policies (e.g. releases/* requires 2 but releases/patches/* requires 3)
    const approvalCountEvaluations = evaluations.filter(e => e.policyType.id === PolicyTypeId.approvalCount) as IApproverCountPolicyEvaluation[];
    let result: IApproverCountPolicyEvaluation | undefined = undefined;
    for (const evaluation of approvalCountEvaluations) {
        if (
            !result ||
            (result.status === PolicyEvaluationStatus.Approved && evaluation.status !== PolicyEvaluationStatus.Approved) ||
            result.minimumApproverCount < evaluation.minimumApproverCount ||
            (result.minimumApproverCount === evaluation.minimumApproverCount && result.creatorVoteCounts && !evaluation.creatorVoteCounts)
        ) {
            result = evaluation;
        }
    }

    return result;
}

const checkFixedTexts: { [key: string]: string | undefined } = {
    [PolicyTypeId.workItemLinking]: Resources.WorkItemsMustBeLinked,
    [PolicyTypeId.commentResolution]: Resources.CommentsMustBeResolved
};

export function processCheckPolicies(
    evaluations: IPullRequestPolicyEvaluation[],
    statuses: GitPullRequestStatus[],
    autoCompleteData: IPullRequestAutoCompleteData | undefined,
    pageContext: IVssPageContext,
    pullRequest: GitPullRequest,
    latestIterationId: number,
    onRequeue: (evaluationId: string) => void,
    setAutoCompleteIgnore: (ignore: boolean, configurationIds: readonly number[]) => void
): CheckEvaluation[] {
    const checks: CheckEvaluation[] = [];

    const selectiveAutoCompleteEnabled = isFeatureFlagEnabled(pageContext, "SourceControl.GitPullRequests.SelectiveAutoComplete", false);
    const statusMenuContributions = loadStatusMenuContributions(pageContext);

    const blockingPolicyIds = new Set((autoCompleteData && autoCompleteData.blockingPolicyIds) || []);
    const ignoredPolicyIds = new Set(
        (selectiveAutoCompleteEnabled && pullRequest.completionOptions && pullRequest.completionOptions.autoCompleteIgnoreConfigIds) || []
    );

    for (const evaluation of getCheckPolicies(evaluations)) {
        let message = checkFixedTexts[evaluation.policyType.id] || evaluation.displayText;
        let status = evaluation.status != undefined ? evaluation.status : PolicyEvaluationStatus.NotApplicable;
        let [statusProps, statusTitle] = checkStatusMap[status];

        if (isBuild(evaluation)) {
            message = evaluation.displayName;
            statusTitle = evaluation.displayText;

            if (status === PolicyEvaluationStatus.Queued && evaluation.buildIsExpired) {
                status = PolicyEvaluationStatus.Rejected;
                statusProps = Statuses.Failed;
                statusTitle = Resources.Expired;
            }
        }

        let genreAndName: string | undefined;
        if (isStatus(evaluation)) {
            genreAndName = `${evaluation.latestStatus.context.genre}/${evaluation.latestStatus.context.name}`;
            statuses = statuses.filter(status => genreAndName !== `${status.context.genre}/${status.context.name}`);
        }

        const href =
            isBuild(evaluation) && evaluation.buildId > 0
                ? getBuildUrl(pageContext, evaluation.buildId.toFixed(), getTFSProject(pageContext)!.name)
                : isStatus(evaluation)
                ? evaluation.latestStatus.targetUrl
                : undefined;

        const configIds: number[] =
            evaluation.configurationId === 0 ? (evaluation as IPullRequestCombinedPolicyEvaluation).configurationIds : [evaluation.configurationId];

        checks.push({
            buildEvaluation: isBuild(evaluation) ? evaluation : undefined,
            expirationDate: isBuild(evaluation) ? evaluation.expirationDate : undefined,
            genreAndName,
            getActions: getCheckActions(
                evaluation,
                pageContext,
                statusMenuContributions,
                pullRequest,
                onRequeue,
                setAutoCompleteIgnore,
                configIds,
                blockingPolicyIds,
                ignoredPolicyIds
            ),
            configurationIds: configIds,
            evaluation,
            id: evaluation.evaluationId,
            isBuild: isBuild(evaluation),
            isRequired: evaluation.isBlocking,
            linkProps: href && { href },
            message,
            orderWeight: getOrderWeight(evaluation),
            status,
            statusProps,
            statusTitle
        });
    }

    // Add as optional checks those statuses that were posted but have no matching policy
    const uniqueMap: { [key: string]: GitPullRequestStatus } = {};
    for (const status of statuses) {
        const genreAndName = `${status.context.genre}/${status.context.name}`;

        // Keep statuses without iteration (entire PR) or for latest iteration, hide those for old iterations.
        // In conflict, prefer status with iteration over those for the entire PR. Then, use the more recent.
        if (!status.iterationId || status.iterationId === latestIterationId) {
            const existing = uniqueMap[genreAndName];
            if (
                !existing ||
                (!existing.iterationId && status.iterationId) ||
                (existing.iterationId === status.iterationId && existing.creationDate < status.creationDate)
            ) {
                uniqueMap[genreAndName] = status;
            }
        }
    }

    for (const genreAndName in uniqueMap) {
        const status = uniqueMap[genreAndName];
        const evaluationStatus = mapStatusStateToEvaluationStatus[status.state || GitStatusState.NotSet];
        if (evaluationStatus !== PolicyEvaluationStatus.NotApplicable) {
            const [statusProps, statusTitle] = checkStatusMap[evaluationStatus];

            checks.push({
                configurationIds: [],
                evaluation: undefined,
                genreAndName,
                getActions: getStatusActions(status, pageContext, statusMenuContributions, pullRequest),
                id: undefined,
                isBuild: false,
                isRequired: false,
                linkProps: status.targetUrl && { href: status.targetUrl },
                message: status.description || genreAndName,
                orderWeight: nonPolicyStatusOrderWeight,
                status: evaluationStatus,
                statusProps,
                statusTitle
            });
        }
    }

    checks.sort((a, b) => b.orderWeight - a.orderWeight || localeIgnoreCaseComparer(a.message, b.message));

    return checks;
}

/*
 * Compute which checks autocomplete is waiting for / blocking on, and which are ignored / won't block
 * autocomplete.
 *
 * Note that some checks are "combined" from multiple underlying policies, and have multiple config IDs.
 * A combined check is considered to be blocking if *any* of the config IDs are blocking auto-complete.
 * A combined check is only considered ignored if *all* of the config IDs are ignored by auto-complete.
 */
export function computeAutoCompleteBlockers(
    checks: CheckEvaluation[],
    blockingPolicyIds: Set<number>,
    ignoredPolicyIds: Set<number>
): {
    blockingChecks: CheckEvaluation[];
    nonBlockingChecks: CheckEvaluation[];
} {
    const blockingChecks: CheckEvaluation[] = [];
    const nonBlockingChecks: CheckEvaluation[] = [];

    checks.forEach(check => {
        if (isBlockingAutoComplete(check.evaluation, blockingPolicyIds, ignoredPolicyIds)) {
            blockingChecks.push(check);
        } else {
            nonBlockingChecks.push(check);
        }
    });

    // Put all the required policies first
    blockingChecks.sort((a, b) => Number(b.isRequired) - Number(a.isRequired) || b.orderWeight - a.orderWeight);

    return { blockingChecks, nonBlockingChecks };
}

/** A note on terminology: this tells you if autocomplete is going to wait for a policy. If the policy is passing,
 *  you could argue that it's not really BLOCKING autocomplete. Maybe should change the names here? */
function isBlockingAutoComplete(
    evaluation: IPullRequestPolicyEvaluation | undefined,
    blockingPolicyIds: Set<number>,
    ignoredPolicyIds: Set<number>
): boolean {
    if (!evaluation) {
        return false;
    }

    const configIds: number[] =
        evaluation.configurationId === 0 ? (evaluation as IPullRequestCombinedPolicyEvaluation).configurationIds : [evaluation.configurationId];
    const isBlocking = configIds.some(id => blockingPolicyIds.has(id));
    const isIgnored = configIds.every(id => ignoredPolicyIds.has(id));

    if (evaluation.isBlocking || isBlocking) {
        return true;
    }
    if (
        isIgnored ||
        ignoredManualBuild(evaluation) ||
        (isStatus(evaluation) && (evaluation.status == undefined || evaluation.status === PolicyEvaluationStatus.Queued))
    ) {
        return false;
    }
    return true;
}

function getCheckActions(
    evaluation: IPullRequestPolicyEvaluation,
    pageContext: IVssPageContext,
    statusMenuContributions: IPullRequestStatusMenuContribution[],
    pullRequest: GitPullRequest,
    onRequeue: (evaluationId: string) => void,
    setAutoCompleteIgnore: (ignore: boolean, configurationIds: readonly number[]) => void,
    configIds: ReadonlyArray<number>,
    blockingPolicyIds: Set<number>,
    autoCompleteIgnoredPolicyIds: Set<number>
) {
    let prMenuItems: ObservableCollection<IMenuItem> | undefined;

    return () => {
        if (!prMenuItems) {
            prMenuItems = new ObservableCollection<IMenuItem>();

            const staticActions: IMenuItem[] = [];

            let combinedRequeueWaitButton = false;
            const isIgnored = configIds.every(id => autoCompleteIgnoredPolicyIds.has(id));
            const blockingAutoComplete = isBlockingAutoComplete(evaluation, blockingPolicyIds, autoCompleteIgnoredPolicyIds);

            if (isBuild(evaluation) && canRequeueBuild(pageContext, evaluation, pullRequest)) {
                let text: string;

                if (
                    pullRequest.autoCompleteSetBy == null ||
                    !isIgnored ||
                    blockingAutoComplete ||
                    evaluation.status === PolicyEvaluationStatus.Approved
                ) {
                    if (evaluation.buildId > 0) {
                        text = Resources.Requeue;
                    } else {
                        text = Resources.Queue;
                    }
                } else {
                    if (evaluation.buildId > 0) {
                        text = Resources.RequeueAndRequire;
                    } else {
                        text = Resources.QueueAndRequire;
                    }
                    combinedRequeueWaitButton = true;
                }

                staticActions.push({
                    id: "requeue",
                    onActivate: () => {
                        // Just always un-ignore when queueing a build. If it's not being ignored, no harm.
                        setAutoCompleteIgnore(false, configIds);
                        onRequeue(evaluation.evaluationId);
                    },
                    text
                });
            }

            if (!combinedRequeueWaitButton && pullRequest.autoCompleteSetBy != null && !evaluation.isBlocking) {
                if (isIgnored && !blockingAutoComplete) {
                    staticActions.push({
                        id: "unignore",
                        onActivate: () => setAutoCompleteIgnore(false, configIds),
                        text: Resources.Require
                    });
                } else if (!isIgnored && blockingAutoComplete) {
                    staticActions.push({
                        id: "ignore",
                        onActivate: () => setAutoCompleteIgnore(true, configIds),
                        text: Resources.MakeOptional
                    });
                }
            }

            prMenuItems.push(staticActions);

            if (isStatus(evaluation)) {
                addStatusActions(evaluation.latestStatus, prMenuItems, pageContext, statusMenuContributions, pullRequest);
            }
        }
        return prMenuItems;
    };
}

function getStatusActions(
    status: GitPullRequestStatus,
    pageContext: IVssPageContext,
    statusMenuContributions: IPullRequestStatusMenuContribution[],
    pullRequest: GitPullRequest
) {
    let actions: ObservableCollection<IMenuItem> | undefined;
    return () => {
        if (!actions) {
            actions = new ObservableCollection<IMenuItem>();
            addStatusActions(status, actions, pageContext, statusMenuContributions, pullRequest);
        }
        return actions;
    };
}

function addStatusActions(
    status: GitPullRequestStatus,
    menuItems: ObservableCollection<IMenuItem>,
    pageContext: IVssPageContext,
    statusMenuContributions: IPullRequestStatusMenuContribution[],
    pullRequest: GitPullRequest
) {
    const extensionContext: IStatusExtensionContext = {
        status,
        pullRequest
    };

    const { genre, name } = status.context;
    const matchContributions = statusMenuContributions.filter(c => c.statusGenre === genre && (!c.statusName || c.statusName === name));
    for (const contribution of matchContributions) {
        addMenuItems(pageContext, menuItems, contribution.id, extensionContext, {
            getExternalMenuItems: (menuService, menuId, contextualData) => {
                if ((menuService as any)._sdkVersion >= 3) {
                    return menuService.getMenuItems(menuId, contextualData);
                }

                // Older SDK versions implemented a getMenuItems with no menuId argument.
                return (menuService as any).getMenuItems(contextualData);
            }
        });
    }
}

export function ignoredManualBuild(policyEval?: IPullRequestPolicyEvaluation): boolean {
    if (policyEval == null || !isBuild(policyEval) || !policyEval.manualQueueOnly) {
        return false;
    }

    // This is a manual-queue build; it has some extra logic around when it should be run.
    // Briefly: if a manual-queue build has never been run, and the user tells autocomplete
    // that it should wait for the build to finish, we run that build for the user.

    return (
        (policyEval.status == null || policyEval.status === PolicyEvaluationStatus.Queued) &&
        (policyEval.buildId | 0) === 0 &&
        !policyEval.serverWillQueueBuild
    );
}

export function updateIgnoreList(currentIgnoredIds: number[], policyConfigIds: number[], ignorePolicy: boolean): number[] {
    currentIgnoredIds = currentIgnoredIds || [];
    policyConfigIds = policyConfigIds || [];

    let updatedIds: Set<number>;

    if (ignorePolicy) {
        updatedIds = new Set([...currentIgnoredIds, ...policyConfigIds]);
    } else {
        updatedIds = new Set(currentIgnoredIds.filter(id => policyConfigIds.indexOf(id) === -1));
    }

    return [...updatedIds.values()].sort((a, b) => a - b);
}

interface IPullRequestStatusMenuContribution {
    id: string;
    statusGenre: string;
    statusName: string | undefined;
}

function loadStatusMenuContributions(pageContext: IVssPageContext): IPullRequestStatusMenuContribution[] {
    const contributionService = pageContext.getService<IVssContributionService>("IVssContributionService");
    const contributionIds = contributionService.getContributionChildren(pullRequestStatusMenuContributionId);
    const contributions: IPullRequestStatusMenuContribution[] = [];
    if (contributionIds) {
        for (const id of contributionIds) {
            const contribution = contributionService.getContribution<IPullRequestStatusMenuContribution>(id);
            if (contribution) {
                contribution.id = id;
                contributions.push(contribution);
            }
        }
    }

    return contributions;
}

const nonPolicyStatusOrderWeight = 3;

function getOrderWeight(evaluation: IPullRequestPolicyEvaluation): number {
    if (isBuild(evaluation)) {
        return evaluation.manualQueueOnly ? 5 : 6;
    } else if (isStatus(evaluation)) {
        return 4;
    } else if (evaluation.policyType.id === PolicyTypeId.workItemLinking) {
        return 2;
    } else if (evaluation.policyType.id === PolicyTypeId.commentResolution) {
        return 1;
    } else {
        return 0;
    }
}

function isBuild(evaluation: IPullRequestPolicyEvaluation): evaluation is BuildPolicyEvaluation {
    return evaluation.policyType.id === PolicyTypeId.build;
}

function isStatus(evaluation: IPullRequestPolicyEvaluation): evaluation is IStatusPolicyEvaluation {
    return evaluation.policyType.id === PolicyTypeId.status;
}

function canRequeueBuild(pageContext: IVssPageContext, buildEvaluation: BuildPolicyEvaluation, pullRequest: GitPullRequest): boolean {
    if (!hasPermission(pageContext, PullRequestPermissions.UsePolicyActions)) {
        return false;
    }

    if (pullRequest.status !== PullRequestStatus.Active || buildEvaluation.serverWillQueueBuild) {
        return false;
    }

    if (buildEvaluation.buildIsExpired) {
        return true;
    }

    return (
        !buildEvaluation.buildId ||
        (buildEvaluation.status !== PolicyEvaluationStatus.Running && buildEvaluation.status !== PolicyEvaluationStatus.Queued)
    );
}

const mapStatusStateToEvaluationStatus = {
    [GitStatusState.NotSet]: PolicyEvaluationStatus.Queued,
    [GitStatusState.Pending]: PolicyEvaluationStatus.Running,
    [GitStatusState.Succeeded]: PolicyEvaluationStatus.Approved,
    [GitStatusState.Failed]: PolicyEvaluationStatus.Rejected,
    [GitStatusState.PartiallySucceeded]: PolicyEvaluationStatus.Rejected,
    [GitStatusState.Error]: PolicyEvaluationStatus.Broken,
    [GitStatusState.NotApplicable]: PolicyEvaluationStatus.NotApplicable
};

const messagesData = [
    {
        policyStatus: PolicyEvaluationStatus.Rejected,
        requiredMessage: {
            one: Resources.RequiredCheckFailed,
            oneOf: Resources.RequiredCheckFailedOf,
            many: Resources.RequiredChecksFailed,
            manyOf: Resources.RequiredChecksFailedOf
        },
        optionalMessage: {
            one: Resources.OptionalCheckFailed,
            many: Resources.OptionalChecksFailed
        }
    },
    {
        policyStatus: PolicyEvaluationStatus.Broken,
        requiredMessage: {
            one: Resources.RequiredCheckBroken,
            oneOf: Resources.RequiredCheckBrokenOf,
            many: Resources.RequiredChecksBroken,
            manyOf: Resources.RequiredChecksBrokenOf
        },
        optionalMessage: {
            one: Resources.OptionalCheckBroken,
            many: Resources.OptionalChecksBroken
        }
    },
    {
        policyStatus: PolicyEvaluationStatus.Running,
        requiredMessage: {
            one: Resources.RequiredCheckRunning,
            oneOf: Resources.RequiredCheckRunningOf,
            many: Resources.RequiredChecksRunning,
            manyOf: Resources.RequiredChecksRunningOf
        },
        optionalMessage: {
            one: Resources.OptionalCheckRunning,
            many: Resources.OptionalChecksRunning
        }
    },
    {
        policyStatus: PolicyEvaluationStatus.Queued,
        requiredMessage: {
            one: Resources.RequiredCheckNotRunYet,
            oneOf: Resources.RequiredCheckNotRunYetOf,
            many: Resources.RequiredChecksNotRunYet,
            manyOf: Resources.RequiredChecksNotRunYetOf
        },
        optionalMessage: {
            one: Resources.OptionalCheckNotRunYet,
            many: Resources.OptionalChecksNotRunYet
        }
    },
    {
        policyStatus: PolicyEvaluationStatus.Approved,
        requiredMessage: {
            one: Resources.RequiredCheckSucceeded,
            many: Resources.RequiredChecksSucceeded
        },
        optionalMessage: {
            one: Resources.OptionalCheckSucceeded,
            many: Resources.OptionalChecksSucceeded
        }
    }
];

function getRequiredChecksMessage(checks: CheckEvaluation[]): [PolicyEvaluationStatus, string] {
    const lookup = getChecksLookup(checks);

    const queuedCount = lookup[PolicyEvaluationStatus.Queued]?.length || 0;
    for (const { policyStatus, requiredMessage } of messagesData) {
        if (lookup[policyStatus]) {
            const count = lookup[policyStatus].length;
            if (policyStatus === PolicyEvaluationStatus.Running && queuedCount > 0) {
                // Running preview will include also Queued, so message must describe both
                if (count === checks.length) {
                    return [
                        policyStatus,
                        count === 1
                            ? format(Resources.RequiredCheckRunningAndQueued, queuedCount)
                            : format(Resources.RequiredChecksRunningAndQueued, count, queuedCount)
                    ];
                } else {
                    return [
                        policyStatus,
                        count === 1
                            ? format(Resources.RequiredCheckRunningAndQueuedOf, checks.length, queuedCount)
                            : format(Resources.RequiredChecksRunningAndQueuedOf, count, checks.length, queuedCount)
                    ];
                }
            } else if (count === checks.length || !requiredMessage.oneOf || !requiredMessage.manyOf) {
                return [policyStatus, count === 1 ? requiredMessage.one : format(requiredMessage.many, count)];
            } else {
                return [
                    policyStatus,
                    count === 1 ? format(requiredMessage.oneOf, checks.length) : format(requiredMessage.manyOf, count, checks.length)
                ];
            }
        }
    }

    return [PolicyEvaluationStatus.Approved, Resources.RequiredChecksNone];
}

function getOptionalChecksMessage(checks: CheckEvaluation[]): [PolicyEvaluationStatus, string] {
    const lookup = getChecksLookup(checks);

    for (const { policyStatus, optionalMessage } of messagesData) {
        if (lookup[policyStatus]) {
            const count = lookup[policyStatus].length;
            return [policyStatus, count === 1 ? optionalMessage.one : format(optionalMessage.many, count)];
        }
    }

    return [PolicyEvaluationStatus.Approved, ""];
}

function getChecksLookup(checks: CheckEvaluation[]): { [key: number]: CheckEvaluation[] } {
    const lookup: { [key: number]: CheckEvaluation[] } = {};
    for (const check of checks) {
        lookup[check.status] = [...(lookup[check.status] || []), check];
    }

    return lookup;
}

const checkStatusMap: { [key: number]: [IStatusProps, string] } = {
    [PolicyEvaluationStatus.Rejected]: [Statuses.Failed, Resources.Failed],
    [PolicyEvaluationStatus.Broken]: [Statuses.Warning, Resources.PolicyBroken],
    [PolicyEvaluationStatus.Running]: [Statuses.Running, Resources.Running],
    [PolicyEvaluationStatus.Queued]: [Statuses.Waiting, Resources.Waiting],
    [PolicyEvaluationStatus.Approved]: [Statuses.Success, Resources.Succeeded],
    [PolicyEvaluationStatus.NotApplicable]: [Statuses.Queued, Resources.NotApplicable]
};

const policyStatusPriorities: { [key: number]: number } = {
    [PolicyEvaluationStatus.Rejected]: 1,
    [PolicyEvaluationStatus.Broken]: 1,
    [PolicyEvaluationStatus.Running]: 2,
    [PolicyEvaluationStatus.Queued]: 2,
    [PolicyEvaluationStatus.Approved]: 3,
    [PolicyEvaluationStatus.NotApplicable]: 4
};
